import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListAndSearchAllWatchlistEntries,
  useCreateWatchlist,
  useCreateWatchlistEntry,
  useListAndSearchAllWatchlists,
  useDeleteWatchlistEntry,
  CACHE_KEYS,
} from "@rentablo/api/apiv1";
import { FundRecommendationResponse } from "@rentablo/api/recommender";

export const useRecommenderWatchlist = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutateAsync: createWatchlistEntry, isPending: isCreating } =
    useCreateWatchlistEntry();
  const { mutateAsync: deleteWatchlistEntry, isPending: isDeleting } =
    useDeleteWatchlistEntry();
  const { mutateAsync: createWatchlist, isPending: isCreatingWatchlist } =
    useCreateWatchlist();
  const { data: watchlistsResponse, isFetching: isLoadingWatchlists } =
    useListAndSearchAllWatchlists();

  const recommenderWatchlist = watchlistsResponse?.watchlists?.find(
    (x) => x.name === t("recommender.watchlistName")
  );

  const { data: recWatchlist, isFetching: isLoadingEntries } =
    useListAndSearchAllWatchlistEntries(
      {
        watchlistId: [recommenderWatchlist?.id!],
      },
      {
        enabled: Boolean(recommenderWatchlist?.id),
      }
    );

  const handleEntry = async (
    recommendation: FundRecommendationResponse,
    listId: number
  ) => {
    if (
      recWatchlist?.watchlistEntries?.find(
        (x) => x.isin === recommendation.fund.isin
      )
    ) {
      await deleteWatchlistEntry({
        id: recWatchlist?.watchlistEntries?.find(
          (x) => x.isin === recommendation.fund.isin
        )?.id!,
      });
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_WATCHLIST_ENTRIES],
      });
    } else {
      await createWatchlistEntry({
        createOrUpdateWatchlistEntryParams: {
          isin: recommendation.fund.isin,
          name: recommendation.fund.name,
          quoteProvider: "ariva",
          type: recommendation.fund.fund_type === "ETF" ? "22_etf" : "21_fund",
          currency: recommendation.fund.fund_currency ?? undefined,
          tickerSymbol: recommendation.fund.ticker ?? undefined,
          watchlistId: listId,
          purchaseDate: new Date().toISOString(),
          purchasePrice: 10000,
        },
      });
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_WATCHLIST_ENTRIES],
      });
    }
  };

  const toggleWatchlistEntry = async (
    recommendation: FundRecommendationResponse
  ) => {
    if (recommenderWatchlist) {
      handleEntry(recommendation, recommenderWatchlist.id!);
    } else {
      const res = await createWatchlist({
        createOrUpdateWatchlistParams: {
          name: t("recommender.watchlistName"),
        },
      });
      handleEntry(recommendation, res.id!);
    }
  };

  const getWatchlistId = async () => {
    if (recommenderWatchlist) {
      return recommenderWatchlist.id;
    } else {
      const res = await createWatchlist({
        createOrUpdateWatchlistParams: {
          name: t("recommender.watchlistName"),
        },
      });
      return res.id;
    }
  };

  const watchlistedIsins = recWatchlist?.watchlistEntries?.map((x) => x.isin);

  return {
    toggleWatchlistEntry,
    isLoading: isLoadingWatchlists || isLoadingEntries,
    isSaving: isCreating || isDeleting || isCreatingWatchlist,
    watchlistedIsins,
    getWatchlistId,
  };
};
